import './css/styles.scss';

/**
 * Megamenyn: paneler på desktop, helskärmslåda på mobil.
 *
 * Samma knappar styr båda lägena. aria-expanded på knappen och hidden på
 * panelen hålls i synk - skärmläsare läser knappens tillstånd, webbläsaren
 * döljer innehållet. Båda behövs.
 */
function megaMenuInit(): void {
	document.querySelectorAll<HTMLElement>('.femp-mega').forEach((nav) => {
		const toggles = Array.from(nav.querySelectorAll<HTMLButtonElement>('.femp-mega__toggle'));
		const hamburgare = nav.querySelector<HTMLButtonElement>('.femp-mega__hamburger');
		const stang = nav.querySelector<HTMLButtonElement>('.femp-mega__stang');
		const overlay = nav.querySelector<HTMLElement>('.femp-mega__overlay');

		const panelFor = (btn: HTMLButtonElement): HTMLElement | null => {
			const id = btn.getAttribute('aria-controls');
			return id ? document.getElementById(id) : null;
		};

		// Nedtoningen hör ihop med panelerna, inte med lådan. Den visas så
		// länge någon panel är öppen.
		const uppdateraOverlay = (): void => {
			if (!overlay) {
				return;
			}
			overlay.hidden = !toggles.some((btn) => btn.getAttribute('aria-expanded') === 'true');
		};

		const stangPaneler = (utom?: HTMLButtonElement): void => {
			toggles.forEach((btn) => {
				if (btn === utom) {
					return;
				}
				btn.setAttribute('aria-expanded', 'false');
				const panel = panelFor(btn);
				if (panel) {
					panel.hidden = true;
				}
			});
			uppdateraOverlay();
		};

		const stangLada = (): void => {
			nav.classList.remove('is-open');
			hamburgare?.setAttribute('aria-expanded', 'false');
			// Låser upp sidans scroll igen.
			document.body.classList.remove('femp-mega-oppen');
		};

		toggles.forEach((btn) => {
			btn.addEventListener('click', () => {
				const oppen = btn.getAttribute('aria-expanded') === 'true';
				stangPaneler(btn);
				btn.setAttribute('aria-expanded', String(!oppen));
				const panel = panelFor(btn);
				if (panel) {
					panel.hidden = oppen;
				}
				uppdateraOverlay();
			});
		});

		hamburgare?.addEventListener('click', () => {
			const oppen = nav.classList.toggle('is-open');
			hamburgare.setAttribute('aria-expanded', String(oppen));
			// Hindrar sidan bakom lådan från att scrolla med.
			document.body.classList.toggle('femp-mega-oppen', oppen);
		});

		stang?.addEventListener('click', () => {
			stangPaneler();
			stangLada();
		});

		overlay?.addEventListener('click', () => stangPaneler());

		document.addEventListener('keydown', (e: KeyboardEvent) => {
			if (e.key !== 'Escape') {
				return;
			}
			// Fokus tillbaka till knappen som var öppen, annars hamnar
			// tangentbordsanvändaren i dokumentets början.
			const oppenKnapp = toggles.find((btn) => btn.getAttribute('aria-expanded') === 'true');
			stangPaneler();
			if (nav.classList.contains('is-open')) {
				stangLada();
				hamburgare?.focus();
			} else if (oppenKnapp) {
				oppenKnapp.focus();
			}
		});

		document.addEventListener('click', (e: MouseEvent) => {
			if (!nav.contains(e.target as Node)) {
				stangPaneler();
			}
		});

		// Lådan hör bara till mobilläget. Dras fönstret upp i desktopbredd
		// ska den inte ligga kvar och låsa scrollen.
		window.addEventListener('resize', () => {
			if (window.innerWidth > 1024 && nav.classList.contains('is-open')) {
				stangLada();
			}
		});
	});
}

/**
 * Mörkar sidhuvudet när sidan scrollats.
 *
 * Stylingen för .fixed-header kommer från mainnavs SCSS, som megamenyns
 * stylesheet importerar. Klassen sätts däremot av mainnavs JS, och den
 * laddas bara när mainnav-blocket finns på sidan. Körs megamenyn i stället
 * måste den sätta klassen själv.
 *
 * Ligger båda blocken på sidan sätter de samma klass på samma element, vilket
 * är ofarligt - classList.add är idempotent.
 */
function stickyHeaderInit(): void {
	const header = document.querySelector<HTMLElement>('header.wp-block-template-part');

	if (!header) {
		return;
	}

	/**
	 * Klassen fast-sidhuvud sätts på megameny-blocket i mall-delen, alltså
	 * inuti sidhuvudet. Finns den ska sidhuvudet vara mörkt direkt, utan att
	 * scrolla.
	 *
	 * Den sitter på blocket och inte på <header>, eftersom <header> genereras
	 * av mall-delsblocket i mallen. Med klassen i partfilen räcker det att
	 * välja mall-del i redigeraren - ingen extra klass att komma ihåg.
	 */
	if (header.querySelector('.femp-mega.fast-sidhuvud')) {
		header.classList.add('fixed-header');
		return;
	}

	// Samma tröskel som mainnav använder, så växlingen sker på samma ställe.
	const troskel = 4;
	let vantar = false;

	const uppdatera = (): void => {
		vantar = false;
		const scroll = window.scrollY || document.documentElement.scrollTop;
		header.classList.toggle('fixed-header', scroll >= troskel);
	};

	uppdatera();

	window.addEventListener(
		'scroll',
		() => {
			if (!vantar) {
				window.requestAnimationFrame(uppdatera);
				vantar = true;
			}
		},
		{ passive: true }
	);

	window.addEventListener('resize', uppdatera);
}

// Scriptet laddas i head, så DOM:en finns inte när modulen körs. Utan den
// här väntan hittar querySelectorAll inga element och inga lyssnare sätts.
function init(): void {
	megaMenuInit();
	stickyHeaderInit();
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', init);
} else {
	init();
}
